import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import { useSupabaseAuth } from '../providers/SupabaseAuthProvider';
import { isCommunityFeatureEnabled } from '../utils/featureHelpers';
import Navigation from '../components/Navigation';
import BottomNavigation from '../components/BottomNavigation';
import MobileHamburgerMenu from '../components/MobileHamburgerMenu';
import ServicesList from '../components/services/ServicesList';
import ServiceAuthModal from '../components/services/ServiceAuthModal';
import ServiceCreateModal from '../components/services/ServiceCreateModal';
import ServicesFAB from '../components/services/ServicesFAB';
import WelcomeModal from '../components/services/WelcomeModal';
import VerificationStatusBanner from '../components/verification/VerificationStatusBanner';
import UserStatusBadge from '../components/UserStatusBadge';
import MobileProfileButton from '../components/MobileProfileButton';
import { servicesService } from '../services/servicesService';
import { getServiceCategoryIcon } from '../config/serviceCategoryIcons';
import { supabase } from '../lib/supabaseClient';
import html2canvas from 'html2canvas';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import '../styles/communityServices.css';

// Helper functions for daily service randomization
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
};

const shuffleWithSeed = (array, seed) => {
  const arr = [...array];
  let m = arr.length, t, i;

  const random = () => {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
  };

  while (m) {
    i = Math.floor(random() * m--);
    t = arr[m];
    arr[m] = arr[i];
    arr[i] = t;
  }

  return arr;
};

const randomizeServicesByDay = (services) => {
  const today = new Date().toDateString();
  const seed = hashString(today);
  return shuffleWithSeed(services, seed);
};

/**
 * Community Services Page Component
 * Implements peer-to-peer service exchange within the community
 */
function CommunityServicesPage() {
  const { config, theme } = useConfig();
  const { session, user, loading: authLoading } = useSupabaseAuth(); // Use auth context with loading state
  const navigate = useNavigate();
  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  // State management
  const [activeTab, setActiveTab] = useState('offered'); // 'needed' or 'offered'
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [memberInfo, setMemberInfo] = useState(null);

  // Check if services are enabled for this center
  useEffect(() => {
    if (config && !isCommunityFeatureEnabled(config, 'services')) {
      navigate('/');
    }
  }, [config, navigate]);

  // Set CSS variables for theme colors
  useEffect(() => {
    if (config?.colors?.primary || theme?.colors?.primary) {
      const primaryColor = config?.colors?.primary || theme?.colors?.primary || '#1e4273';
      document.documentElement.style.setProperty('--center-primary-color', primaryColor);
    }
  }, [config, theme]);

  // Fetch fresh member data from database to ensure latest profile info (including location)
  const refreshMemberFromDatabase = async (userId) => {
    try {
      console.log('[CommunityServices] Refreshing member data from database for user:', userId);

      const { data: userData, error } = await supabase
        .from('verified_users')
        .select('*, community_members!user_id(id, center_id, membership_status, city, state)')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('[CommunityServices] Error fetching member data:', error);
        return false;
      }

      if (userData?.community_members) {
        const existingMember = userData.community_members.find(
          m => m.center_id === config.center_id
        );

        if (existingMember) {
          const freshMemberData = {
            id: existingMember.id,
            user_id: userId,
            name: userData.first_name,
            last_initial: userData.last_name?.charAt(0) || '',
            email: user.email,
            status: existingMember.membership_status,
            city: existingMember.city || userData.city,
            state: existingMember.state || userData.state
          };

          console.log('[CommunityServices] Fresh member data retrieved:', freshMemberData);
          setMemberInfo(freshMemberData);
          servicesService.saveMemberInfo(freshMemberData); // Update localStorage with fresh data
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('[CommunityServices] Exception refreshing member data:', error);
      return false;
    }
  };

  // Check authentication status using context
  useEffect(() => {
    const checkAuthAndMember = async () => {
      console.log('[CommunityServices] Session from context:', session, user);

      if (session && user) {
        // Try to get from localStorage first for immediate UI update
        const member = await servicesService.getMemberInfo();
        console.log('[CommunityServices] Retrieved member info from storage:', member);

        if (member) {
          setMemberInfo(member);
          // Refresh from database in background to get latest data (important for location fields)
          refreshMemberFromDatabase(user.id);
        } else {
          // No localStorage, fetch from database
          console.log('[CommunityServices] No member info in localStorage, fetching from database');
          const refreshed = await refreshMemberFromDatabase(user.id);
          if (!refreshed) {
            console.log('[CommunityServices] No member info in database, user can browse freely');
            // Auth modal will show only when user tries to post a service
          }
        }
      } else {
        // No session, clear member info
        setMemberInfo(null);
      }
    };

    checkAuthAndMember();
  }, [session, user, config]); // Re-run when session/user changes from context

  // Debug: Track auth state changes for troubleshooting
  useEffect(() => {
    console.log('[CommunityServices] 🔄 Auth state changed:', {
      has_session: !!session,
      user_email: session?.user?.email,
      has_memberInfo: !!memberInfo,
      memberInfo_id: memberInfo?.id,
      memberInfo_name: memberInfo?.name
    });
  }, [session, memberInfo]);

  // Fetch categories with popularity sorting (re-fetch when activeTab changes)
  useEffect(() => {
    if (!config?.center_id) return;

    const fetchCategories = async () => {
      try {
        const cats = await servicesService.getCategories(config.center_id, activeTab);
        const popularity = await servicesService.getCategoryPopularity(config.center_id);

        // Create popularity map for sorting
        const popularityMap = {};
        popularity.forEach(p => {
          popularityMap[p.category] = p.whatsapp_clicks || p.total_clicks || 0;
        });

        // Sort categories by popularity (WhatsApp clicks)
        const sortedCats = cats.sort((a, b) => {
          const aClicks = popularityMap[a.name] || 0;
          const bClicks = popularityMap[b.name] || 0;
          return bClicks - aClicks; // Descending order
        });

        setCategories([
          { id: 'All', name: 'All', icon: null }, // No icon for "All"
          ...sortedCats
        ]);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, [config, activeTab]);

  // Fetch services based on active tab and category
  useEffect(() => {
    if (!config?.center_id) return;

    const fetchServices = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await servicesService.getServices(
          config.center_id,
          activeTab,
          selectedCategory === 'All' ? null : selectedCategory
        );

        // Apply daily randomization to prevent Craigslist-style gaming
        const randomized = randomizeServicesByDay(data);
        setServices(randomized);
      } catch (err) {
        console.error('Failed to fetch services:', err);
        setError('Failed to load services. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [config, activeTab, selectedCategory]);

  // Handle deep linking - Auto-scroll and expand shared service
  useEffect(() => {
    // Check for shared service in URL parameters
    const params = new URLSearchParams(window.location.search);
    const sharedServiceId = params.get('id');
    const serviceType = params.get('type');

    if (sharedServiceId && services.length > 0) {
      // Switch to correct tab if needed
      if (serviceType && serviceType !== activeTab) {
        setActiveTab(serviceType);
        return; // Let the next render handle the scroll after tab switch
      }

      // Wait for DOM to render, then find and scroll to service
      setTimeout(() => {
        const serviceElement = document.querySelector(`[data-service-id="${sharedServiceId}"]`);

        if (serviceElement) {
          // Scroll to center of viewport
          serviceElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Auto-expand the service
          const expandButton = serviceElement.querySelector('.horizontal-row');
          if (expandButton && !serviceElement.classList.contains('expanded')) {
            expandButton.click();
          }

          // Add highlight animation
          serviceElement.classList.add('shared-service-highlight');

          // Remove highlight after 3 seconds
          setTimeout(() => {
            serviceElement.classList.remove('shared-service-highlight');
          }, 3000);

          // Clean URL (optional - remove query params after processing)
          // window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          // Service not found (may be removed or doesn't exist)
          console.warn('Shared service not found:', sharedServiceId);
        }
      }, 500); // Wait for DOM to be ready
    }
  }, [services, activeTab]);

  // Check for email confirmation completion and show welcome modal
  useEffect(() => {
    // WAIT for auth to finish loading (Expert-recommended fix for race condition)
    if (authLoading) {
      console.log('[CommunityServices] Auth still loading, waiting...');
      return;
    }

    // Check if user just confirmed email (from our email confirmation page)
    const showWelcomeAfterConfirmation = sessionStorage.getItem('showWelcomeAfterConfirmation');
    const justConfirmed = sessionStorage.getItem('auth:justConfirmed');

    if ((showWelcomeAfterConfirmation || justConfirmed) && user && memberInfo) {
      console.log('[CommunityServices] User just confirmed email - showing welcome modal');
      setShowWelcomeModal(true);

      // Clear the flags
      sessionStorage.removeItem('showWelcomeAfterConfirmation');
      sessionStorage.removeItem('auth:justConfirmed');

      // Clean up URL if needed
      if (window.location.hash.includes('access_token')) {
        window.history.replaceState({}, '', '/community/services');
      }
    }

    // Clean up any old pendingSignup data (from old flow)
    if (user) {
      const oldPendingSignup = sessionStorage.getItem('pendingSignup');
      if (oldPendingSignup) {
        console.log('[CommunityServices] Cleaning up old pendingSignup data');
        sessionStorage.removeItem('pendingSignup');
      }
    }
  }, [authLoading, user, config, activeTab, selectedCategory]);

  // Handle offering help for a service (no auth required — just opens contact link)
  const handleOfferHelp = (service) => {
    // Determine contact method based on service preferences
    let contactMethod = 'whatsapp';
    let contactUrl = '';

    const phone = service.member?.phone_display || '';
    const message = `Salaam, I saw your request for "${service.title}" on ${config.center_name} Community Services and would like to help.`;

    if (service.allow_whatsapp) {
      contactMethod = 'whatsapp';
      contactUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    } else if (service.allow_calls) {
      contactMethod = 'call';
      contactUrl = `tel:${phone}`;
    } else if (service.allow_sms) {
      contactMethod = 'sms';
      contactUrl = `sms:${phone}?body=${encodeURIComponent(message)}`;
    } else {
      alert('No contact method available for this service.');
      return;
    }

    // Log contact analytics with category for popularity tracking
    servicesService.logContactAnalytics(
      service.id,
      contactMethod,
      service.category,
      config.center_id
    );

    // Legacy logging
    servicesService.logContact(service.id, contactMethod);

    // Open contact method
    window.open(contactUrl, '_blank');
  };

  // Handle creating a new service post with type
  const handleCreatePost = (type = activeTab) => {
    if (!session) {
      setShowAuthModal(true);
      return;
    }
    setActiveTab(type); // Switch to the appropriate tab
    setShowCreateModal(true);
  };

  // Handle successful authentication
  const handleAuthSuccess = async (memberData) => {
    console.log('[CommunityServices] handleAuthSuccess called with:', memberData);
    setMemberInfo(memberData);
    // Save member info to localStorage for persistence
    servicesService.saveMemberInfo(memberData);
    setShowAuthModal(false);

    // Show welcome modal for new registrations
    if (memberData.message && memberData.message.includes('Profile created')) {
      console.log('[CommunityServices] New registration detected - showing welcome modal');
      setShowWelcomeModal(true);
    }

    // Refresh services to show member-specific options
    if (config?.center_id) {
      const data = await servicesService.getServices(
        config.center_id,
        activeTab,
        selectedCategory === 'All' ? null : selectedCategory
      );
      setServices(data);
    }
  };

  // Handle service update (delete, mark fulfilled, etc.)
  const handleServiceUpdate = async () => {
    if (config?.center_id) {
      setLoading(true);
      try {
        const data = await servicesService.getServices(
          config.center_id,
          activeTab,
          selectedCategory === 'All' ? null : selectedCategory
        );

        // Apply daily randomization to maintain fair ordering
        const randomized = randomizeServicesByDay(data);
        setServices(randomized);
      } catch (err) {
        console.error('Failed to refresh services:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle service creation
  const handleServiceCreated = (newService) => {
    setShowCreateModal(false);

    // Show context-aware success message
    const context = newService._context || {};
    let message = '';

    if (context.membership_status === 'pending' && context.service_status === 'pending') {
      // Scenario 1: Profile NOT approved + Service pending
      message = '✅ Service Request Received!\n\nYour request has been saved and will be posted once:\n• Your profile is approved (usually 1-2 business days)\n• Your service request is reviewed\n\nWe\'ll notify you when it\'s live!';
    } else if (context.membership_status === 'validated' && context.service_status === 'pending') {
      // Scenario 2: Profile approved + Service needs moderation
      message = '✅ Service Request Submitted!\n\nYour request is being reviewed and will be posted soon.\n\nThis usually takes a few hours.';
    } else if (context.membership_status === 'validated' && context.service_status === 'active') {
      // Scenario 3: Profile approved + Service auto-approved
      message = '✅ Service Posted Successfully!\n\nYour request is now live and visible to the community!';
    } else {
      // Fallback
      message = '✅ Service posted successfully!';
    }

    // Show alert with message
    alert(message);

    // Refresh services list
    if (config?.center_id) {
      servicesService.getServices(
        config.center_id,
        activeTab,
        selectedCategory === 'All' ? null : selectedCategory
      ).then(setServices);
    }
  };

  // Handle share with screenshot
  const handleShare = async () => {
    try {
      console.log('[CommunityServices] Starting share with screenshot...');

      const servicesSection = document.querySelector('[data-services-section]') ||
                             document.querySelector('.community-services-content') ||
                             document.querySelector('.services-container');

      let file = null;

      if (!servicesSection) {
        console.warn('[CommunityServices] Services section element not found for screenshot');
      } else {
        console.log('[CommunityServices] Found services section, capturing screenshot...');

        try {
          // Hide FAB button temporarily during screenshot (like ServiceCard does)
          const fabButton = document.querySelector('.services-fab-container') ||
                           document.querySelector('[aria-label="Share services"]')?.parentElement;
          const userBadge = document.querySelector('.user-status-badge');

          const elementsToHide = [fabButton, userBadge].filter(Boolean);
          elementsToHide.forEach(el => {
            if (el) el.style.visibility = 'hidden';
          });

          const canvas = await html2canvas(servicesSection, {
            backgroundColor: '#ffffff',
            scale: 2,
            logging: false,
            useCORS: true,
            allowTaint: true
          });

          // Restore hidden elements
          elementsToHide.forEach(el => {
            if (el) el.style.visibility = 'visible';
          });

          console.log('[CommunityServices] Screenshot captured, creating blob...');

          const blob = await new Promise(resolve =>
            canvas.toBlob(resolve, 'image/png')
          );

          if (blob) {
            console.log('[CommunityServices] Blob created successfully, size:', blob.size);
            const categoryText = selectedCategory === 'All' ? 'Community' : selectedCategory;
            const tabText = activeTab === 'offered' ? 'Services-Offered' : 'Services-Needed';
            file = new File([blob], `${config.name}-${categoryText}-${tabText}.png`, {
              type: 'image/png'
            });
          } else {
            console.warn('[CommunityServices] Blob creation failed');
          }
        } catch (err) {
          console.error('[CommunityServices] Screenshot capture error:', err);
          // Restore elements even if error occurs
          const fabButton = document.querySelector('.services-fab-container') ||
                           document.querySelector('[aria-label="Share services"]')?.parentElement;
          const userBadge = document.querySelector('.user-status-badge');
          [fabButton, userBadge].forEach(el => {
            if (el) el.style.visibility = 'visible';
          });
        }
      }

      // Generate smart share message based on context
      let shareText = `Check out community services at ${config.name}`;
      if (selectedCategory !== 'All') {
        shareText = `Check out ${selectedCategory} services in our community`;
      }
      if (activeTab === 'needed') {
        shareText = `Help needed: Community is looking for these services`;
      }

      const shareData = {
        title: `${config.name} - Community Services`,
        text: shareText,
        url: window.location.href  // Preserves filters in URL
      };

      // Share with or without image
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...shareData, files: [file] });
      } else if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy link
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[CommunityServices] Share failed:', err);
      }
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      console.log('[CommunityServices] Signing out...');
      await servicesService.clearSession();

      // Clear local state
      setMemberInfo(null);

      // Increased delay to ensure sign-out propagates fully
      await new Promise(resolve => setTimeout(resolve, 200));

      // Use href to force complete navigation (more reliable in PWA)
      window.location.href = window.location.pathname;
    } catch (error) {
      console.error('[CommunityServices] Sign out error:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  // Welcome Card component for empty states
  const WelcomeCard = () => {
    const getWelcomeMessage = () => {
      if (!session) {
        return {
          title: activeTab === 'needed' ? 'No services needed yet' : 'No services offered yet',
          subtitle: 'Join the community to be the first to post!',
          showButton: false
        };
      }

      return {
        title: activeTab === 'needed' ? 'No services needed yet' : 'No services offered yet',
        subtitle: 'Be the first to help your community!',
        showButton: false  // Button is now shown above the services list
      };
    };

    const { title, subtitle, showButton } = getWelcomeMessage();

    return (
      <div className="welcome-card">
        <div className="welcome-icon">
          {activeTab === 'needed' ? '🤝' : '💝'}
        </div>
        <h3>{title}</h3>
        <p>{subtitle}</p>
        {showButton && (
          <button className="welcome-cta-btn" onClick={() => handleCreatePost()}>
            Post a Service
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="community-services-page">
      {/* Navigation */}
      {isMobile ? (
        <MobileHamburgerMenu />
      ) : (
        <Navigation showSearch={false} />
      )}

      {/* Header */}
      <div className="services-header">
        {/* Top-left: Join to Post button (unauthenticated) or Profile button (authenticated) */}
        {!session ? (
          <button
            className="join-to-post-btn"
            onClick={() => setShowAuthModal(true)}
          >
            <span className="btn-icon-circle">
              <PersonAddIcon style={{ fontSize: 18, color: '#10B981' }} />
            </span>
            <span className="btn-text">Join to Post</span>
          </button>
        ) : (
          isMobile && user && (
            <MobileProfileButton
              user={user}
              memberInfo={memberInfo}
              onSignOut={handleSignOut}
            />
          )
        )}
        <h1>Community Services</h1>
      </div>

      {/* Verification Status Banner - Only show for authenticated users */}
      {session && <VerificationStatusBanner />}

      {/* Tab Navigation */}
      <div className="services-tabs">
        <button
          className={`tab-btn ${activeTab === 'offered' ? 'active' : ''}`}
          onClick={() => setActiveTab('offered')}
        >
          Services Offered
        </button>
        <button
          className={`tab-btn ${activeTab === 'needed' ? 'active' : ''}`}
          onClick={() => setActiveTab('needed')}
        >
          Services Needed
        </button>
      </div>

      {/* Category Filter */}
      <div className="category-filter">
        <div className="category-scroll">
          {categories.map(cat => {
            const isAll = cat.id === 'all';
            const categoryConfig = getServiceCategoryIcon(cat.name || cat.id);
            const IconComponent = categoryConfig?.icon;

            // Truncate category name (4 letters + "...")
            const truncatedName = isAll
              ? cat.name
              : cat.name.length > 4
              ? `${cat.name.substring(0, 4)}...`
              : cat.name;

            return (
              <button
                key={cat.id}
                className={`category-chip ${selectedCategory === cat.name ? 'active' : ''} ${isAll ? 'all-chip' : ''}`}
                onClick={() => setSelectedCategory(cat.name)}
                title={cat.name} // Tooltip shows full name
              >
                {!isAll && IconComponent && (
                  <span className="category-icon">
                    <IconComponent style={{ fontSize: '16px', color: categoryConfig.color }} />
                  </span>
                )}
                <span className="category-name">{truncatedName}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Services List */}
      <div className="services-container" data-services-section>
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading services...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Post Service Button - Always show for authenticated users */}
            {session && memberInfo && (
              <div className="post-service-prompt">
                <button
                  className="post-service-btn"
                  onClick={() => handleCreatePost(activeTab)}
                >
                  {activeTab === 'offered' ? (
                    <>
                      <span className="plus-icon">+</span> Offer a Service to Your Community
                    </>
                  ) : (
                    '🤝 Request Help from Your Community'
                  )}
                </button>
              </div>
            )}

            {/* Show services if they exist, or welcome card if empty */}
            {services.length === 0 ? (
              <WelcomeCard />
            ) : (
              <ServicesList
                services={services}
                type={activeTab}
                onOfferHelp={handleOfferHelp}
                isAuthenticated={!!session}
                memberInfo={memberInfo}
                onUpdate={handleServiceUpdate}
              />
            )}
          </>
        )}
      </div>

      {/* Services FAB - Share button */}
      <div className="services-fab-container">
        <ServicesFAB
          onShare={handleShare}
          centerName={config.name}
          theme={theme}
        />
      </div>

      {/* Modals */}
      {showAuthModal && (
        <ServiceAuthModal
          config={config}
          theme={theme}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {showCreateModal && (
        <ServiceCreateModal
          config={config}
          categories={categories.filter(c => c.id !== 'All')}
          serviceType={activeTab}
          memberInfo={memberInfo}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleServiceCreated}
          onAuthRequired={() => {
            console.log('[CommunityServices] Auth required, showing auth modal');
            setShowCreateModal(false);
            setShowAuthModal(true);
          }}
        />
      )}

      {/* Welcome Modal for new users after email confirmation */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        userName={user?.name || user?.email}
        centerName={config?.name}
      />

      {/* User Status Badge - Shows logged-in user info (Desktop only) */}
      {!isMobile && session && user && (
        <UserStatusBadge
          user={user}
          memberInfo={memberInfo}
          onSignOut={handleSignOut}
        />
      )}

      {/* Bottom Navigation for mobile */}
      {isMobile && <BottomNavigation />}
    </div>
  );
}

export default CommunityServicesPage;